import React from 'react';
import { Box, Paper, Typography, SxProps } from '@mui/material';
import { Circle } from '@mui/icons-material';
import { Theme, alpha, useTheme } from '@mui/material/styles';
import { AlertStatus } from '../types/alerts';
import { getCardBorder } from '../DashboardV2/types';

interface AlertStatusLightProps {
  status: AlertStatus | null;
  sx?: SxProps<Theme>;
}

export const AlertStatusLight: React.FC<AlertStatusLightProps> = ({
  status,
  sx = {}
}) => {
  const theme = useTheme();

  if (!status) {
    return (
      <Paper elevation={0} sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        border: getCardBorder(theme),
        borderRadius: 2,
        ...sx
      }}>
        <Circle sx={{
          color: theme.palette.grey[400],
          fontSize: 16,
        }} />
        <Typography variant="body2" fontWeight="medium" color="text.primary">
          Loading...
        </Typography>
      </Paper>
    );
  }

  const alerts = status?.alerts;

  // Handle case where alerts data is missing
  if (!alerts) {
    return (
      <Paper elevation={0} sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        border: getCardBorder(theme),
        borderRadius: 2,
        ...sx
      }}>
        <Circle sx={{
          color: theme.palette.grey[400],
          fontSize: 16,
        }} />
        <Typography variant="body2" fontWeight="medium" color="text.primary">
          No alert data available
        </Typography>
      </Paper>
    );
  }

  const activeAlerts = (alerts.new ?? 0) + (alerts.acknowledged ?? 0);
  const needsReview = alerts.auto_resolved ?? 0;

  const getSeverityColor = (severity: string): string => {
    switch (severity.toLowerCase()) {
      case 'critical': return theme.palette.error.dark;
      case 'error': return theme.palette.error.main;
      case 'warning': return theme.palette.warning.main;
      case 'info': return theme.palette.info.main;
      default: return theme.palette.grey[500];
    }
  };

  const lightColor = activeAlerts > 0
    ? getSeverityColor(alerts.highestSeverity)
    : needsReview > 0
      ? theme.palette.info.main
      : theme.palette.success.main;

  return (
    <Paper elevation={0} sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      border: (activeAlerts > 0 || needsReview > 0) ? `2px solid ${lightColor}` : getCardBorder(theme),
      borderRadius: 2,
      backgroundColor: (activeAlerts > 0 || needsReview > 0) ? alpha(lightColor, 0.04) : undefined,
      transition: 'all 0.2s ease-in-out',
      ...sx
    }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(lightColor, 0.1),
          animation: activeAlerts > 0 ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.5 },
            '100%': { opacity: 1 },
          }
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: lightColor,
          }}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{
            color: activeAlerts > 0 ? lightColor : theme.palette.success.dark
          }}
        >
          {activeAlerts > 0
            ? `${activeAlerts} Active Alert${activeAlerts !== 1 ? 's' : ''}`
            : needsReview > 0
              ? `${needsReview} Need${needsReview !== 1 ? '' : 's'} Review`
              : 'All Clear'
          }
        </Typography>
        {(activeAlerts > 0 || needsReview > 0) && (
          <>
            <Typography variant="caption" color="text.secondary">
              {activeAlerts > 0
                ? `${alerts.new} new, ${alerts.acknowledged} acknowledged${needsReview > 0 ? `, ${needsReview} needs review` : ''}`
                : `${needsReview} auto-resolved alert${needsReview !== 1 ? 's' : ''} pending review`
              }
            </Typography>
            {activeAlerts > 0 && alerts.highestSeverity && (
              <Typography
                variant="caption"
                fontWeight="medium"
                sx={{
                  display: 'block',
                  color: lightColor,
                  mt: 0.5,
                }}
              >
                Highest: {alerts.highestSeverity.toUpperCase()}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
};
